import { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { useContent } from '@/lib/content';

const inputCls = 'w-full bg-ink border border-white/15 px-3 py-2.5 text-sm text-cream placeholder:text-white/30 focus:border-champagne focus:outline-none transition-colors';

const DEFAULTS = { price_2d: 550, price_3d: 1100, min_2d: 8, min_3d: 16, tiers: [] };

export const CalculatorTab = () => {
  const { calculator, refresh } = useContent();
  const [form, setForm] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setForm({ ...DEFAULTS, ...(calculator || {}), tiers: (calculator?.tiers || []).map((t) => ({ ...t })) });
  }, [calculator]);

  const setNum = (k) => (e) => setForm({ ...form, [k]: Number(e.target.value) || 0 });
  const setTier = (i, k) => (e) => {
    const tiers = form.tiers.map((t, idx) => (idx === i ? { ...t, [k]: e.target.value } : t));
    setForm({ ...form, tiers });
  };
  const setTierInclusions = (i) => (e) => {
    const tiers = form.tiers.map((t, idx) => (idx === i ? { ...t, inclusions: e.target.value.split('\n') } : t));
    setForm({ ...form, tiers });
  };
  const addTier = () => setForm({ ...form, tiers: [...form.tiers, { decor_type: '2d', min_ft: 8, max_ft: '', inclusions: [] }] });
  const removeTier = (i) => setForm({ ...form, tiers: form.tiers.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const payload = {
        price_2d: form.price_2d, price_3d: form.price_3d, min_2d: form.min_2d, min_3d: form.min_3d,
        tiers: form.tiers.map((t) => ({
          decor_type: t.decor_type,
          min_ft: Number(t.min_ft) || 0,
          max_ft: t.max_ft === '' || t.max_ft === null || t.max_ft === undefined ? null : Number(t.max_ft),
          inclusions: (t.inclusions || []).map((s) => s.trim()).filter(Boolean),
        })),
      };
      await api.put('/admin/calculator', payload);
      await refresh();
      setMsg('Calculator settings saved — live on the website.');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid="admin-calculator-form" onSubmit={save}>
      <p className="text-white/50 text-sm mb-6">Controls the public Birthday Decor Calculator at /birthday-calculator.</p>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1.5">2D Price per Running Ft (₹)</label>
          <input data-testid="calc-field-price-2d" type="number" className={inputCls} value={form.price_2d} onChange={setNum('price_2d')} />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1.5">3D Price per Running Ft (₹)</label>
          <input data-testid="calc-field-price-3d" type="number" className={inputCls} value={form.price_3d} onChange={setNum('price_3d')} />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1.5">2D Minimum Stage (ft)</label>
          <input data-testid="calc-field-min-2d" type="number" className={inputCls} value={form.min_2d} onChange={setNum('min_2d')} />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1.5">3D Minimum Stage (ft)</label>
          <input data-testid="calc-field-min-3d" type="number" className={inputCls} value={form.min_3d} onChange={setNum('min_3d')} />
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.25em] text-champagne">Inclusions by Stage Size</p>
        <button type="button" data-testid="calc-add-tier" onClick={addTier} className="inline-flex items-center gap-2 border border-champagne/60 text-champagne px-4 py-2 text-[11px] uppercase tracking-[0.2em] hover:bg-champagne hover:text-ink transition-colors">
          <Plus size={13} /> Add Range
        </button>
      </div>

      <div className="mt-4 space-y-5">
        {form.tiers.map((t, i) => (
          <div key={i} data-testid={`calc-tier-${i}`} className="border border-white/10 p-4 sm:p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5">Decor Type</label>
                <select data-testid={`calc-tier-type-${i}`} className={`${inputCls} cursor-pointer`} value={t.decor_type} onChange={setTier(i, 'decor_type')}>
                  <option value="2d" className="bg-ink">2D</option>
                  <option value="3d" className="bg-ink">3D</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5">From (ft)</label>
                <input data-testid={`calc-tier-min-${i}`} type="number" className={inputCls} value={t.min_ft} onChange={setTier(i, 'min_ft')} />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5">To (ft, blank = unlimited)</label>
                <input data-testid={`calc-tier-max-${i}`} type="number" className={inputCls} value={t.max_ft ?? ''} onChange={setTier(i, 'max_ft')} placeholder="∞" />
              </div>
              <div className="flex items-end justify-end">
                <button type="button" data-testid={`calc-tier-remove-${i}`} onClick={() => removeTier(i)} className="p-2.5 text-white/50 hover:text-red-400 transition-colors" aria-label="Remove range">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5 mt-4">Inclusions (one per line)</label>
            <textarea
              data-testid={`calc-tier-inclusions-${i}`}
              rows={4}
              className={`${inputCls} resize-none`}
              value={(t.inclusions || []).join('\n')}
              onChange={setTierInclusions(i)}
              placeholder={'2D theme backdrop\nBalloon arch\nCake table styling'}
            />
          </div>
        ))}
        {form.tiers.length === 0 && <p className="text-white/40 text-sm">No ranges yet — click "Add Range".</p>}
      </div>

      {msg && <p data-testid="calculator-saved" className="mt-5 text-sm text-champagne">{msg}</p>}
      {error && <p className="mt-5 text-sm text-red-400">{error}</p>}
      <button data-testid="calculator-save-btn" type="submit" disabled={busy} className="mt-7 inline-flex items-center gap-2 bg-champagne text-ink px-6 py-3 text-[11px] uppercase tracking-[0.2em] font-semibold hover:bg-cream transition-colors disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin" /> : null} Save Calculator Settings
      </button>
    </form>
  );
};
